"use client";

import { useState } from "react";
import { Button } from "../../ui/button";
import { Card, CardContent } from "../../ui/card";
import { Copy, Check, Mail } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@dub/utils";

interface Contact {
  id: string;
  name: string;
  email: string;
  isPrimary: boolean;
}

interface EmailListProps {
  data: {
    title?: string;
    contacts?: Contact[];
  };
}

export const EmailList = ({ data }: EmailListProps) => {
  const contacts = data.contacts || [];
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const handleCopy = (id: string, email: string) => {
    navigator.clipboard.writeText(email);
    setCopiedId(id);
    toast.success("Email copied to clipboard");
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleEmailAll = () => {
    if (contacts.length === 0) {
      toast.error("No contacts to email");
      return;
    }

    const primaryContacts = contacts.filter((c) => c.isPrimary);
    // If no primary set, take first
    const to = (primaryContacts.length > 0 ? primaryContacts : [contacts[0]])
      .map((c) => c.email)
      .join(",");

    const cc = contacts
      .filter(
        (c) =>
          !c.isPrimary &&
          (primaryContacts.length === 0 ? c !== contacts[0] : true)
      )
      .map((c) => c.email)
      .join(",");

    const mailtoLink = `mailto:${to}?cc=${cc}`;
    window.location.href = mailtoLink;
  };

  if (contacts.length === 0) {
    return null;
  }

  return (
    <Card className="my-8">
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-neutral-900">
            {data.title || "Key Contacts"}
          </h3>
          <Button
            variant="outline"
            className="h-8 text-xs"
            icon={<Mail className="h-3.5 w-3.5" />}
            text="Email All"
            onClick={handleEmailAll}
          />
        </div>

        <div className="space-y-2">
          {contacts.map((contact) => (
            <div
              key={contact.id || contact.email}
              className="group flex items-center justify-between p-2.5 rounded-lg border border-neutral-200 bg-white hover:border-neutral-300 transition-all"
            >
              <div className="flex items-center gap-3 overflow-hidden">
                <div
                  className={cn(
                    "flex items-center justify-center w-8 h-8 rounded-full text-xs font-medium shrink-0",
                    contact.isPrimary
                      ? "bg-blue-100 text-blue-700"
                      : "bg-neutral-100 text-neutral-600"
                  )}
                >
                  {contact.name
                    ? contact.name.charAt(0).toUpperCase()
                    : contact.email.charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm text-neutral-900 truncate">
                      {contact.name || contact.email}
                    </span>
                    {contact.isPrimary && (
                      <span className="text-[10px] uppercase tracking-wider font-bold text-blue-600 bg-blue-100 px-1.5 py-0.5 rounded-md">
                        Primary
                      </span>
                    )}
                  </div>
                  {contact.name && (
                    <div className="text-xs text-neutral-500 truncate">
                      {contact.email}
                    </div>
                  )}
                </div>
              </div>

              <button
                type="button"
                onClick={() => handleCopy(contact.id || contact.email, contact.email)}
                className="p-1.5 rounded-md text-neutral-400 hover:text-neutral-900 hover:bg-neutral-100 transition-colors"
                title="Copy email"
              >
                {copiedId === (contact.id || contact.email) ? (
                  <Check className="h-4 w-4 text-emerald-600" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </button>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
